import type { MouseEvent, ReactNode } from "react";
import { clsx } from "clsx";
import styled from "styled-components";

import LoadingSpinner from "@/shared/ui/LoadingSpinner";

import theme from "../../config/theme";

import cls from "./Button.module.scss";

export interface ButtonProps {
  className?: string;
  type?: "button" | "submit" | "reset";
  isLoading?: boolean;
  children: ReactNode;
  disabled?: boolean;
  onClick?: (e: MouseEvent) => void;
}

const StyledButton = styled("button")<ButtonProps>`
  ${theme.typography.bodyMedium};
  border-radius: 24px;
  border: none;
  width: 160px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background-color: var(--color-magenta);
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;

  &:focus {
    outline: none;
  }

  ${(p) =>
    p.disabled &&
    `
    opacity: 0.5;
    pointer-events: none;
    cursor: default;
  `}
`;

export const Button = (props: ButtonProps) => {
  const { isLoading, children, ...rest } = props;

  if (isLoading) {
    return (
      <StyledButton {...rest}>
        <LoadingSpinner spinnerColor="white" />
      </StyledButton>
    );
  }

  return <StyledButton {...rest}>{children}</StyledButton>;
};

export default Button;

export enum ButtonTheme {
  SMALL = "Small",
}

interface SmallProps {
  className?: string;
  dataTestId?: string;
  type?: "button" | "submit";
  children?: ReactNode;
  theme?: ButtonTheme;
  onClick?: (e: MouseEvent) => void;
}

export const Small = (props: SmallProps) => {
  const { className, dataTestId, type = "button", children, theme = ButtonTheme.SMALL, onClick } = props;

  const mods = {
    [cls[theme]]: true,
  };

  return (
    <button
      type={type}
      data-testid={dataTestId}
      className={clsx(cls.Button, mods, [className])}
      onClick={onClick}
    >
      {children}
    </button>
  );
};
